/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/lib/router.ts`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useSyncExternalStore } from 'react';

/** Minimal history router: /, /admin, /account and /t/:tid/:page */
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());
window.addEventListener('popstate', notify);

type EmbeddedNavInterceptor = (to: string, replace: boolean) => boolean;
let embeddedInterceptor: EmbeddedNavInterceptor | null = null;

export function setEmbeddedNavigateInterceptor(fn: EmbeddedNavInterceptor | null) {
  embeddedInterceptor = fn;
}

export function navigateFullPage(to: string, replace = false) {
  if (to === location.pathname + location.search) return;
  if (replace) history.replaceState(null, '', to); else history.pushState(null, '', to);
  notify();
  window.scrollTo({ top: 0 });
}

export function navigate(to: string, replace = false) {
  if (embeddedInterceptor && embeddedInterceptor(to, replace)) {
    return;
  }
  navigateFullPage(to, replace);
}

export function usePath() {
  return useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, () => location.pathname + location.search);
}

export function parseRoute(path: string) {
  const [p, q] = path.split('?');
  const query = new URLSearchParams(q ?? '');
  const isStandaloneHost =
    typeof window !== 'undefined' &&
    (/^wertschriften/i.test(window.location.hostname) || /\.trycloudflare\.com$/i.test(window.location.hostname));
  if (
    p === '/wertschriften' ||
    p === '/wertschriften-desk' ||
    p === '/standalone/wertschriften' ||
    query.get('standalone') === '1' ||
    (isStandaloneHost && p === '/' && query.get('kontor') !== '1')
  ) {
    const m = p.match(/^\/t\/([^/]+)/);
    return { tid: m?.[1] ?? query.get('tid') ?? 'alpenwerk', page: 'wertschriften-standalone', query };
  }
  const m = p.match(/^\/t\/([^/]+)(?:\/([^/]+))?/);
  if (m) {
    return { tid: m[1], page: m[2] ?? 'cockpit', query };
  }
  const directMatch = p.match(/^\/([a-z0-9-]+)$/);
  const directSlug = directMatch?.[1] ?? '';
  const TENANT_PAGES = new Set([
    'cockpit', 'insights', 'agent', 'review', 'questions', 'practice',
    'journal', 'documents', 'accounts', 'reports', 'close', 'yearend',
    'controlling', 'invoices', 'receivables', 'payments',
    'vat', 'payroll', 'bank', 'bankaccounts', 'independence', 'authorities',
    'digital-twins', 'aiops', 'finma', 'payroll-qa', 'dlt', 'dlt-satoshi-cockpit', 'sovereign-dlt', 'autonomous',
    'transmutation', 'millionx', 'wow', 'auditsim', 'audit', 'integrity',
    'setup', 'privacy', 'settings',
    'accountant-training', 'triangular-tax', 'triangular-tax-agent', 'enterprise-monkey', 'agent-office', 'office', 'wertschriften', 'showcases',
    'freehand-discovery',
  ]);
  if (TENANT_PAGES.has(directSlug)) {
    return { tid: query.get('tid') ?? 'alpenwerk', page: directSlug, query };
  }
  const firm = p === '/admin' ? 'admin' : p === '/account' ? 'account' : 'firm';
  return { tid: null, page: firm, query };
}
